import { useQuery } from '@apollo/client/react'
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageLayout,
  Section,
  buttonClasses,
  linkClasses,
  useLanguage,
  useTranslate,
} from '@athena/ui'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'

import { PracticeRowLink } from '../app/links.js'
import { usePageTitle } from '../app/page-title.js'
import { PracticeFilters } from '../features/practices/PracticeFilters.js'
import { SubjectGroupedPractices } from '../features/practices/SubjectGroupedPractices.js'
import {
  MyPracticesDocument,
  PracticeCatalogDocument,
  PracticeFilterSubjectsDocument,
  type PracticeCardFieldsFragment,
} from '../graphql/generated/operations.js'
import { useFormat } from '../lib/format.js'
import { practiceStatus } from '../lib/practice-status.js'

/**
 * The practice list. Search and the two category filters live in the URL
 * rather than in component state, so a narrowed list is a link a tutor can
 * keep or share, and the header's search field can drive this screen from
 * anywhere in the app.
 *
 * The split between them is deliberate. Subject and level go to the server,
 * which is the only place that can narrow rows it is willing to return. The
 * name search stays in the browser because it is a substring match over a list
 * already in hand, and sending it would be a round trip per keystroke.
 */
export function PracticesScreen() {
  const t = useTranslate()
  const language = useLanguage()
  const format = useFormat()
  usePageTitle(t('practice.listTitle'))
  const navigate = useNavigate()
  const { q, subjectId } = useSearch({ from: '/$language/tutor/practices' })

  const { data: catalog } = useQuery(PracticeCatalogDocument, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000,
  })
  const { data: historicalCatalog } = useQuery(PracticeFilterSubjectsDocument, {
    fetchPolicy: 'cache-and-network',
  })
  const { data, loading, error } = useQuery(MyPracticesDocument, {
    variables: { subjectId: subjectId ?? null },
  })

  const settingsLink = (
    <Link to="/$language/practices/settings" params={{ language }} className={linkClasses()}>
      {t('practice.preferences.title')}
    </Link>
  )

  if (loading) {
    return (
      <PageLayout width="operational">
        {settingsLink}
        <LoadingState className="p-0 py-8">{t('practice.loadingList')}</LoadingState>
      </PageLayout>
    )
  }
  if (error) {
    return (
      <PageLayout width="operational">
        {settingsLink}
        <ErrorState className="p-0 py-8">{t('practice.listError')}</ErrorState>
      </PageLayout>
    )
  }

  // Three lists, because the server decides which practice is in use, which
  // the plan has set aside and which is retired. Search filters all of them,
  // so a tutor looking for a name finds it whichever shelf it is on.
  const all = data?.active ?? []
  const query = q?.toLowerCase() ?? ''
  const matching = (list: readonly PracticeCardFieldsFragment[]) =>
    query ? list.filter((p) => p.title.toLowerCase().includes(query)) : list

  const practices = matching(all)
  const setAside = matching(data?.setAside ?? [])
  const archived = matching(data?.archived ?? [])

  const filtered = subjectId !== undefined

  // An empty shelf and a shelf narrowed to nothing are different situations,
  // and only one of them means "you have no practices".
  if (all.length === 0 && setAside.length === 0 && archived.length === 0 && !filtered) {
    return (
      <PageLayout width="operational">
        {settingsLink}
        <EmptyState
          variant="page"
          title={t('dashboard.firstPracticeTitle')}
          description={t('dashboard.firstPracticeDescription')}
          action={
            <Link
              to="/$language/practices/new"
              params={{ language }}
              className={buttonClasses({ size: 'lg' })}
            >
              {t('practice.create')}
            </Link>
          }
        />
      </PageLayout>
    )
  }

  return (
    <PageLayout width="operational">
      {/* No "Create practice" here: the shell pins it to every tutor screen,
          the sidebar at desktop and the bottom bar on a phone, and the
          dashboard already leaves it to the shell for the same reason. A
          second copy on this page was the same action twice. The empty shelf
          below still offers it, because there it is the only thing to do. */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <PracticeFilters
          value={{ subjectId }}
          onChange={(next) =>
            void navigate({
              to: '/$language/practices',
              params: { language },
              search: {
                ...(q ? { q } : {}),
                ...(next.subjectId ? { subjectId: next.subjectId } : {}),
              },
            })
          }
          subjects={[
            ...new Map(
              [
                ...(catalog?.availableSubjects ?? []),
                ...(historicalCatalog?.practiceFilterSubjects ?? []),
                ...[
                  ...(data?.active ?? []),
                  ...(data?.archived ?? []),
                  ...(data?.setAside ?? []),
                ].map((practice) => practice.subject),
              ].map((subject) => [subject.id, subject]),
            ).values(),
          ].sort((a, b) => a.name.localeCompare(b.name, language))}
        />
        {settingsLink}
      </div>

      {q ? (
        <p className="flex flex-wrap items-center gap-3 text-body-sm text-text-secondary">
          <span>
            {t('practice.searchSummary')} <span className="font-semibold text-text">{q}</span>
          </span>
          <Link
            to="/$language/practices"
            params={{ language }}
            search={{}}
            className={linkClasses()}
          >
            {t('common.clear')}
          </Link>
        </p>
      ) : null}

      {practices.length === 0 && setAside.length === 0 && archived.length === 0 ? (
        <EmptyState
          variant="page"
          title={t('practice.nothingMatchesTitle')}
          description={
            q
              ? filtered
                ? t('practice.nothingMatchesSearchFiltered', [q])
                : t('practice.nothingMatchesSearch', [q])
              : t('practice.nothingMatchesFilters')
          }
          action={
            <Link
              to="/$language/practices"
              params={{ language }}
              search={{}}
              className={buttonClasses({ variant: 'secondary' })}
            >
              {q && !filtered ? t('common.clearSearch') : t('common.clearFilters')}
            </Link>
          }
        />
      ) : (
        <SubjectGroupedPractices
          headingLevel={2}
          searching={query !== ''}
          practices={practices}
          renderPractice={(practice) => (
            <PracticeRowLink
              to="/$language/practices/$practiceId"
              params={{ language, practiceId: practice.id }}
              title={practice.title}
              status={practiceStatus(practice)}
              meta={format.practiceMeta({
                questionCount: practice.publishedVersion?.questionCount ?? null,
                assignedCount: practice.assignedStudents.length,
                updatedAt: practice.updatedAt,
              })}
            />
          )}
        />
      )}

      {setAside.length > 0 ? (
        <Section
          id="set-aside"
          title={t('practice.setAsideHeading')}
          className="border-t border-border pt-6 md:pt-8"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-body-sm text-text-secondary">{t('practice.setAsideDescription')}</p>
            <Link
              to="/$language/plan"
              params={{ language }}
              hash="practices-set-aside"
              className={buttonClasses({ variant: 'ghost', size: 'sm' })}
            >
              {t('subscription.bringBackOnPlan')}
            </Link>
          </div>

          <SubjectGroupedPractices
            searching={query !== ''}
            practices={setAside}
            renderPractice={(practice) => (
              <PracticeRowLink
                to="/$language/practices/$practiceId"
                params={{ language, practiceId: practice.id }}
                title={practice.title}
                status="setAside"
                meta={format.practiceMeta({
                  questionCount: practice.publishedVersion?.questionCount ?? null,
                  assignedCount: practice.assignedStudents.length,
                  updatedAt: practice.updatedAt,
                })}
              />
            )}
          />
        </Section>
      ) : null}

      {archived.length > 0 ? (
        <Section id="archived" title={t('practice.archivedHeading')} className="pt-2">
          <p className="text-body-sm text-text-secondary">{t('practice.archivedDescription')}</p>

          <SubjectGroupedPractices
            searching={query !== ''}
            practices={archived}
            renderPractice={(practice) => (
              <PracticeRowLink
                to="/$language/practices/$practiceId"
                params={{ language, practiceId: practice.id }}
                title={practice.title}
                status="archived"
                meta={format.accessSummary(practice)}
              />
            )}
          />
        </Section>
      ) : null}
    </PageLayout>
  )
}
